import getAuthApi from '@sleepstation/api-auth'
import {
  JWT_NAME_ACCESS,
  JWT_NAME_REFRESH,
  getAuthorizer,
} from '@sleepstation/auth-jwt'
import loggingApi from '@sleepstation/logger'
import { splitOnEqualsSign } from '@sleepstation/utils'
import memoize from 'fast-memoize'
import { dissoc } from 'ramda'
import { isNotNil } from 'ramda-adjunct'
import urlParse from 'url-parse'
import { AUTH_TIMEOUT_DURATION } from '../const/auth'
import LOG_GROUP from '../const/logGroup'
import { redirectToLoginURL } from '../utils/auth'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const logger = loggingApi.getLogger({
  group: LOG_GROUP.AUTH,
})

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const dissocString = dissoc(`string`)

// We only want to update the user object if the refresh token has changed, so
// we memoize.
const setUserOnErrorReporter = memoize(
  ({ errorReporter, refreshTokenDecoded }) => {
    logger.debug(`➜ Setting user on getAuthFailureHandler`)
    logger.debug(`⚿ refreshTokenDecoded`, refreshTokenDecoded)
    if (refreshTokenDecoded?.sub) {
      // Don't include the token string as this is a credential
      const refreshTokenDecodedWithoutString = dissocString(refreshTokenDecoded)

      logger.debug(
        `⚿ refreshTokenDecodedWithoutString`,
        refreshTokenDecodedWithoutString
      )
      logger.debug(`➜ setting User on error reporter`)
      errorReporter.setUser({
        id: refreshTokenDecoded.sub,
        refreshTokenDecoded: refreshTokenDecodedWithoutString,
      })
    }
  }
)

const getAuthFailureHandler =
  (errorReporter) =>
  ({ error, refreshTokenDecoded } = {}) => {
    logger.debug(`➜ Executing getAuthFailureHandler`)
    setUserOnErrorReporter({ errorReporter, refreshTokenDecoded })

    errorReporter.send(error, {
      reporterCalledFrom: `getAuthFailureHandler`,
    })

    redirectToLoginURL()
  }

const getAuthSuccessHandler =
  (errorReporter) =>
  ({ refreshTokenDecoded } = {}) => {
    logger.debug(`➜ Executing authSuccessHandler`)
    setUserOnErrorReporter({ errorReporter, refreshTokenDecoded })
  }

const getAuthContext = ({
  appOrigin,
  authApi,
  localStorageApi,
  errorReporter,
}) => {
  // Create an audience key for use in requests to the auth API, using the app
  // origin without the protocol
  const { host: audience } = urlParse(appOrigin, true)

  // Configure our authorizer that will be used on startup and before any
  // request to our API
  const authorizer = getAuthorizer({
    localStorageService: localStorageApi,
    authApi,
    audience,
  })

  const onAuthFailure = getAuthFailureHandler(errorReporter)
  const onAuthSuccess = getAuthSuccessHandler(errorReporter)

  return {
    authorizer,
    onAuthFailure,
    onAuthSuccess,
    localStorageApi,
    timoutDurationMS: AUTH_TIMEOUT_DURATION,
  }
}

const extractTokenFromHash = () => {
  const { hash } = window.location
  const token = hash ? splitOnEqualsSign(hash)[1] : null
  logger.debug(`⚿ Extracting refresh token from hash: '${token}'`)
  return token
}

const persistRefreshTokenFromHash = (localStorageApi) => {
  logger.debug(`➜ Checking for refresh token in hash`)
  const token = extractTokenFromHash()
  if (isNotNil(token)) {
    logger.debug(`⚿ Found refresh token in hash`)
    logger.debug(`⚿ Writing refresh token to local storage`)
    localStorageApi.setValue(JWT_NAME_REFRESH, token)
  } else {
    logger.debug(`⚿ No refresh token in hash`)
  }
}

// -----------------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------------

const initialiseAuth = memoize(({ errorReporter, localStorageApi, origin }) => {
  // Congigure our authorisation API that allows the client to renew Refresh
  // JWTs and request Access JWTs
  const authApi = getAuthApi({
    origin,
    errorReporter,
  })

  // Create an auth context
  const authContext = getAuthContext({
    appOrigin: origin,
    authApi,
    localStorageApi,
    errorReporter,
  })

  // Delete any Access JWT to ensure a new one is requested when a browser
  // window/tab is first opened.
  logger.debug(`⚿ Purging any existing access tokens`)
  localStorageApi.clearValue(JWT_NAME_ACCESS)

  // If we have been returned here from visiting the login page, we will have
  // an encoded refresh token in the hash which we need to persist to local
  // storage for use in our auth flow.
  persistRefreshTokenFromHash(localStorageApi)

  return { authApi, authContext }
})

export default initialiseAuth
